/**
 * The browser side of Web Push — card #99.
 *
 * Slate's service worker (`public/sw.js`) does one thing: show a push as a
 * notification and open the card when it is clicked. No caching, no offline —
 * R37 keeps v1 a manifest-only PWA, and a worker that caches is a worker that
 * can serve yesterday's app.
 */
import type { CreatePushSubscriptionRequest } from '@slate/shared';

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export type PushPermission = 'unsupported' | NotificationPermission;

export function pushPermission(): PushPermission {
  return pushSupported() ? Notification.permission : 'unsupported';
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (existing) return existing;
  await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  return navigator.serviceWorker.ready;
}

/** This browser's subscription, if it has one. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const existing = await navigator.serviceWorker.getRegistration('/');
  return existing ? existing.pushManager.getSubscription() : null;
}

/**
 * Ask permission, subscribe, and return what the API stores. A subscription
 * made with a different key (the server's keys were changed) cannot be reused,
 * so it is dropped and made again.
 */
export async function subscribeThisBrowser(
  publicKey: string,
): Promise<CreatePushSubscriptionRequest> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(
      permission === 'denied'
        ? 'Notifications are blocked for Slate in this browser. Allow them in the site settings, then try again.'
        : 'Notifications were not allowed.',
    );
  }
  const worker = await registration();
  const key = urlBase64ToUint8Array(publicKey);
  let subscription = await worker.pushManager.getSubscription();
  if (subscription && !sameKey(subscription, key)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  subscription ??= await worker.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key,
  });
  return toRequest(subscription);
}

/** Unsubscribe this browser; returns the endpoint it had, so the API can forget it. */
export async function unsubscribeThisBrowser(): Promise<string | null> {
  const subscription = await currentSubscription();
  if (!subscription) return null;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  return endpoint;
}

export function toRequest(subscription: PushSubscription): CreatePushSubscriptionRequest {
  const json = subscription.toJSON();
  return {
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
    label: browserLabel(),
  };
}

/** "Chrome on macOS" — enough to tell your laptop from your phone in a list. */
export function browserLabel(): string {
  const agent = navigator.userAgent;
  const browser = /Edg\//.test(agent)
    ? 'Edge'
    : /Firefox\//.test(agent)
      ? 'Firefox'
      : /Chrome\//.test(agent)
        ? 'Chrome'
        : /Safari\//.test(agent)
          ? 'Safari'
          : 'A browser';
  const system = /iPhone|iPad/.test(agent)
    ? 'iOS'
    : /Android/.test(agent)
      ? 'Android'
      : /Mac OS X/.test(agent)
        ? 'macOS'
        : /Windows/.test(agent)
          ? 'Windows'
          : /Linux/.test(agent)
            ? 'Linux'
            : 'an unknown system';
  return `${browser} on ${system}`;
}

function sameKey(subscription: PushSubscription, key: Uint8Array): boolean {
  const current = subscription.options.applicationServerKey;
  if (!current) return false;
  const bytes = new Uint8Array(current);
  return bytes.length === key.length && bytes.every((byte, index) => byte === key[index]);
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = `${base64}${'='.repeat((4 - (base64.length % 4)) % 4)}`
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes;
}
